import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { siteConfig } from '@/mocks/siteConfig';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const pageTitle = article.seoTitle || `${article.title} | ${siteConfig.siteName}`;
  const pageDesc = article.seoDescription || article.excerpt;
  const pageKeywords = article.seoKeywords || article.tags.join(',');

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: siteConfig.siteName,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.siteUrl}/article/${article.slug}`,
    },
  };

  const categoryName = getCategoryName(article.category);

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`${siteConfig.siteUrl}/article/${article.slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />

      <Navbar />

      <main className="pt-24">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="mt-6 mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="text-xs font-medium text-primary-500 bg-primary-100 px-2.5 py-1 rounded-full hover:bg-primary-200 transition-colors"
              >
                {categoryName}
              </Link>
              {article.tags.slice(0, 2).map((tag) => (
                <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-sm font-medium text-foreground-900">{article.author}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-sm text-foreground-500">{article.publishDate}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-sm text-foreground-500">{article.readTime} 分钟阅读</span>
            </div>
          </header>

          {/* Cover Image */}
          <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-600 font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-foreground-600 bg-background-100 px-3 py-1.5 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Author Card */}
          <div className="mt-8 p-5 bg-background-100 rounded-lg flex items-start gap-4">
            <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="font-heading text-sm font-semibold text-foreground-900 mb-1">
                {article.author}
              </p>
              <p className="text-xs text-foreground-600 leading-relaxed">
                秘密教学核心作者，专注于认知科学、学习方法和隐性知识传递领域的研究与写作。
              </p>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <h2 className="font-heading text-lg md:text-2xl font-bold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <ArticleCard key={related.slug} article={related} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}